/**
 * Разбор ошибок CSV-импорта (POST /nsi/csv_file, GET /nsi/csv_load_status).
 * Ядро (core) отдаёт такие ошибки сырым текстом Postgres-исключения.
 *
 * С 30.09.2026 core сам подставляет имя атрибута (см. core/.../
 * nsi_meta/entities/{helper,crud}.sql):
 *
 *   RaiseException (ERROR_TOOLTIP:Обязательный аттрибут Сезонность - не
 *   может быть пустым! data: {"e_id": null, "attrs": [{"value": "906700",
 *   "atype_id": 276, "attribute": "os_CardNo"}, ...], "type_id": 308}
 *   CONTEXT: ...)
 *
 *   RaiseException (ERROR_TOOLTIP:Не найдена сущность Keen. Поле: Бренд
 *   (at_id = 2), sort=1 CONTEXT: ...)
 *
 * До этой даты (или если фронтенд задеплоен раньше core) имя атрибута в
 * тексте отсутствует — тогда остаётся только числовой atype_id, и
 * компонент-рендерер (ImportCsvErrorDetails) резолвит имя сам через
 * /nsi_meta/type_schema (см. entitiesService.getTypeSchema) как fallback.
 */

export interface CsvImportErrorAttr {
    atypeId: number;
    /** Есть, только если core уже обновлён (см. заголовок файла) */
    attributeName?: string;
    value: unknown;
}

export interface CsvImportEntityDataError {
    kind: "entity_data";
    /** Текст до 'data:' — обычно уже человекочитаемый, напр. "Обязательный
     * аттрибут Сезонность - не может быть пустым!" */
    summary: string;
    typeId: number | null;
    attrs: CsvImportErrorAttr[];
}

export interface CsvImportAliasNotFoundError {
    kind: "alias_not_found";
    summary: string;
    /** Значение, которое не удалось найти в справочнике (напр. 'Keen') */
    alias: string;
    atypeId: number;
    /** Есть, только если core уже обновлён (см. заголовок файла) */
    attributeName?: string;
    sort: number;
}

export type CsvImportParsedError = CsvImportEntityDataError | CsvImportAliasNotFoundError;

const TOOLTIP_PREFIX = "ERROR_TOOLTIP:";
// новый формат (с именем поля) проверяется первым, старый — fallback
const ALIAS_NOT_FOUND_WITH_NAME_RE =
    /^(Не найдена сущность (.+?)\. Поле: (.+?) \(at_id = (\d+)\), sort=(\d+))/;
const ALIAS_NOT_FOUND_RE = /^(Не найдена сущность (.+?)\. at_id = (\d+), sort=(\d+))/;

/** Вырезает сбалансированный JSON-объект начиная с первого '{' после from,
 * с учётом вложенных {} и строк в кавычках (внутри data: попадаются HTML-
 * описания товаров с произвольным текстом). */
const extractJsonObject = (text: string, from: number): { value: unknown } | null => {
    const start = text.indexOf("{", from);
    if (start === -1) return null;

    let depth = 0;
    let inString = false;
    let escape = false;

    for (let i = start; i < text.length; i += 1) {
        const ch = text[i];
        if (inString) {
            if (escape) {
                escape = false;
            } else if (ch === "\\") {
                escape = true;
            } else if (ch === '"') {
                inString = false;
            }
            continue;
        }
        if (ch === '"') {
            inString = true;
        } else if (ch === "{") {
            depth += 1;
        } else if (ch === "}") {
            depth -= 1;
            if (depth === 0) {
                const raw = text.slice(start, i + 1);
                try {
                    return { value: JSON.parse(raw) };
                } catch {
                    return null;
                }
            }
        }
    }
    return null;
};

export const parseCsvImportError = (raw: string | undefined | null): CsvImportParsedError | null => {
    if (!raw) return null;
    const idx = raw.indexOf(TOOLTIP_PREFIX);
    if (idx === -1) return null;

    const tail = raw.slice(idx + TOOLTIP_PREFIX.length);

    const aliasWithNameMatch = tail.match(ALIAS_NOT_FOUND_WITH_NAME_RE);
    if (aliasWithNameMatch) {
        return {
            kind: "alias_not_found",
            summary: aliasWithNameMatch[1],
            alias: aliasWithNameMatch[2],
            attributeName: aliasWithNameMatch[3],
            atypeId: Number(aliasWithNameMatch[4]),
            sort: Number(aliasWithNameMatch[5]),
        };
    }

    const aliasMatch = tail.match(ALIAS_NOT_FOUND_RE);
    if (aliasMatch) {
        return {
            kind: "alias_not_found",
            summary: aliasMatch[1],
            alias: aliasMatch[2],
            atypeId: Number(aliasMatch[3]),
            sort: Number(aliasMatch[4]),
        };
    }

    const dataIdx = tail.indexOf("data:");
    if (dataIdx === -1) return null;

    const summary = tail.slice(0, dataIdx).trim();
    const parsed = extractJsonObject(tail, dataIdx);
    if (!parsed || typeof parsed.value !== "object" || parsed.value === null) return null;

    const obj = parsed.value as { attrs?: unknown; type_id?: unknown };
    if (!Array.isArray(obj.attrs)) return null;

    const attrs: CsvImportErrorAttr[] = obj.attrs
        .filter((a): a is { atype_id: unknown; attribute?: unknown; value: unknown } => typeof a === "object" && a !== null)
        .map((a) => ({
            atypeId: Number((a as { atype_id: unknown }).atype_id),
            attributeName:
                typeof (a as { attribute?: unknown }).attribute === "string"
                    ? ((a as { attribute?: string }).attribute as string)
                    : undefined,
            value: (a as { value: unknown }).value,
        }));

    const typeId = obj.type_id !== undefined && obj.type_id !== null ? Number(obj.type_id) : null;

    return { kind: "entity_data", summary, typeId, attrs };
};
